import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { accessAPI } from '../services/api';
import { AccessContext } from './AccessContext';

export const ACTIVE_PERSONA_STORAGE_KEY = 'activePersona';

const writeStoredPersona = (role) => {
  try {
    if (role) {
      localStorage.setItem(ACTIVE_PERSONA_STORAGE_KEY, role);
    } else {
      localStorage.removeItem(ACTIVE_PERSONA_STORAGE_KEY);
    }
  } catch {
    // localStorage unavailable — the header simply is not sent.
  }
};

const EMPTY_ACCESS = {
  bypass: false,
  businessCases: [],
  personas: [],
  activePersona: null,
  services: [],
  diagnostics: null,
};

/**
 * What the signed-in user may use (Business Cases, personas, WP3 services and
 * permissions), as computed by GET /access/me. The chosen persona is mirrored
 * to localStorage so httpClient can send `X-Active-Persona` without a React
 * render; the server ignores a value the user does not hold and the provider
 * adopts whatever the server returns. Depends on AuthProvider.
 */
export const AccessProvider = ({ children }) => {
  const { user } = useAuth();
  const userId = user?.id;
  const [access, setAccess] = useState(EMPTY_ACCESS);
  const [loading, setLoading] = useState(true);
  const latestRequest = useRef(0);

  const load = useCallback(async () => {
    const requestId = latestRequest.current + 1;
    latestRequest.current = requestId;

    let data = await accessAPI.getMine();
    // Several eligible personas and none chosen yet: act as the first one
    // explicitly so the header, the sidebar and the catalogue all agree.
    if (!data.activePersona && data.personas?.length > 1) {
      writeStoredPersona(data.personas[0].role);
      data = await accessAPI.getMine();
    }
    if (requestId !== latestRequest.current) return; // a newer load superseded this one

    writeStoredPersona(data.activePersona || null);
    setAccess({ ...EMPTY_ACCESS, ...data });
  }, []);

  const refreshAccess = useCallback(async () => {
    try {
      await load();
    } catch {
      setAccess(EMPTY_ACCESS);
    }
  }, [load]);

  useEffect(() => {
    let cancelled = false;
    if (!userId) {
      latestRequest.current += 1;
      setAccess(EMPTY_ACCESS);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    load()
      .catch(() => {
        if (!cancelled) setAccess(EMPTY_ACCESS);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [userId, load]);

  const setActivePersona = useCallback(async (role) => {
    // Only a persona the server offered can be chosen.
    if (!access.personas.some((persona) => persona.role === role)) return;
    writeStoredPersona(role);
    await refreshAccess();
  }, [access.personas, refreshAccess]);

  const can = useCallback((serviceRef, permission) => {
    const entry = access.services.find((service) => service.code === serviceRef || service.id === serviceRef);
    if (!entry) return false;
    return permission ? entry.permissions.includes(permission) : true;
  }, [access.services]);

  const value = useMemo(
    () => ({ ...access, loading, setActivePersona, refreshAccess, can }),
    [access, loading, setActivePersona, refreshAccess, can]
  );

  return <AccessContext.Provider value={value}>{children}</AccessContext.Provider>;
};
